import { motion } from "framer-motion";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { Users, Recycle, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import { SCOPE_MUNICIPALITIES } from "@/config/scope";
import { fetcher } from "@/lib/api";

// Reutilizamos las fotos elegidas en ConcelloCards para consistencia visual
const PHOTO_BY_KEY = {
  cambados: "/photos/cambados/mirador-de-a-pastora-2153.jpg",
  meis: "/photos/meis/banco-monte-castrove-4152.jpg",
  "vilanova-de-arousa": "/photos/vilanova/mirador-de-as-sinas-vilanova-de-arousa-746209.jpg",
};

function fmtInt(n) {
  return n == null
    ? "—"
    : new Intl.NumberFormat("es-ES").format(Math.round(n));
}
function fmtDec(n, d = 1) {
  return n == null
    ? "—"
    : new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      }).format(n);
}

/**
 * "Impacto por concello" premium block for /impacto.
 * Full-bleed photo per card + overlay + 3 KPIs.
 */
export default function ImpactoConcelloShowcase() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data } = useSWR("/public/impacto/carbon-by-concello", fetcher, {
    revalidateOnFocus: false,
  });
  const byKey = {};
  (data?.items || []).forEach((it) => {
    byKey[it.key] = it;
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
      {SCOPE_MUNICIPALITIES.map((c, idx) => {
        const stats = byKey[c.key] || {};
        const photo = PHOTO_BY_KEY[c.key];
        const hasData = stats.kg_total != null && stats.kg_total > 0;
        return (
          <motion.div
            key={c.key}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{
              duration: 0.6,
              delay: idx * 0.08,
              ease: [0.22, 1, 0.36, 1],
            }}
            data-testid={`impacto-showcase-${c.key}`}
          >
            <Link
              to={`/impacto?concello=${c.key}`}
              className="group block relative rounded-3xl overflow-hidden aspect-[3/4] shadow-md hover:shadow-2xl transition-shadow duration-500"
            >
              <img
                src={photo}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.06]"
                loading="lazy"
              />
              {/* Overlay for legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-salnes-ink/95 via-salnes-ink/40 to-salnes-ink/10" />

              <div className="absolute inset-0 flex flex-col justify-between p-7 lg:p-8">
                {/* Top: name + color chip */}
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-white/70 text-[10px] uppercase tracking-[0.28em]">
                      {lang === "es" ? "Municipio" : "Concello"}
                    </div>
                    <div className="font-serif text-white text-3xl sm:text-4xl mt-1 leading-tight drop-shadow">
                      {c.name}
                    </div>
                  </div>
                  <div
                    className="w-3 h-3 rounded-full ring-2 ring-white/80 mt-2"
                    style={{ background: c.color }}
                    aria-hidden
                  />
                </div>

                {/* Bottom: KPIs */}
                <div className="space-y-3">
                  <KpiRow
                    icon={Users}
                    label={lang === "es" ? "Habitantes" : "Habitantes"}
                    value={fmtInt(c.population)}
                    testid={`showcase-${c.key}-hab`}
                  />
                  <KpiRow
                    icon={Recycle}
                    label={lang === "es" ? "Recogidos" : "Recollidos"}
                    value={
                      hasData
                        ? `${fmtDec(stats.t_total, 1)} t`
                        : lang === "es"
                          ? "Sin datos"
                          : "Sen datos"
                    }
                    dim={!hasData}
                    testid={`showcase-${c.key}-kg`}
                  />
                  <KpiRow
                    icon={Leaf}
                    label="CO₂e"
                    value={
                      hasData
                        ? `${fmtInt(stats.co2e_kg)} kg`
                        : lang === "es"
                          ? "Sin datos"
                          : "Sen datos"
                    }
                    dim={!hasData}
                    testid={`showcase-${c.key}-co2`}
                  />
                </div>
              </div>
            </Link>
          </motion.div>
        );
      })}
    </div>
  );
}

function KpiRow({ icon: Icon, label, value, dim, testid }) {
  return (
    <div
      className="flex items-center gap-3 text-white"
      data-testid={testid}
    >
      <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center shrink-0">
        <Icon size={14} className="text-white/85" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-[0.22em] text-white/60">
          {label}
        </div>
        <div
          className={`text-xl font-serif tabular-nums leading-tight ${
            dim ? "text-white/50" : "text-white"
          }`}
        >
          {value}
        </div>
      </div>
    </div>
  );
}
